import { useState, useEffect } from "react"
import { CreditCard, Check, Zap, Crown, Star, AlertCircle, X, Receipt } from "lucide-react"
import api from "../services/api"
import Badge from "../components/common/Badge"
import { formatDate } from "../utils/formatters"
import toast from "react-hot-toast"

const PLANS = [
  {
    id: "free", name: "Free", price: 0, period: "forever",
    color: "border-surface-200", badge: "gray", icon: Star,
    features: ["1 bot", "5 backtests/month", "1 exchange", "Paper trading only", "Basic strategies", "Community support"],
    limits: { bots: 1, backtests: 5, exchanges: 1 },
  },
  {
    id: "pro", name: "Pro", price: 49, period: "month",
    color: "border-brand-500/50", badge: "blue", icon: Zap,
    features: ["10 bots", "Unlimited backtests", "5 exchanges", "Live trading", "All strategies", "Priority support", "Advanced reports", "Signal creation"],
    limits: { bots: 10, backtests: -1, exchanges: 5 },
    popular: true,
  },
  {
    id: "elite", name: "Elite", price: 149, period: "month",
    color: "border-trading-yellow/40", badge: "yellow", icon: Crown,
    features: ["Unlimited bots", "Unlimited backtests", "Unlimited exchanges", "Live trading", "All strategies", "Dedicated support", "AI Assistant", "Marketplace access", "Custom strategies", "API access"],
    limits: { bots: -1, backtests: -1, exchanges: -1 },
  },
]

const MOCK_INVOICES = [
  { id: "inv_001", date: "2024-10-01", amount: 49, status: "paid", plan: "Pro" },
  { id: "inv_002", date: "2024-09-01", amount: 49, status: "paid", plan: "Pro" },
  { id: "inv_003", date: "2024-08-01", amount: 49, status: "paid", plan: "Pro" },
]

function UsageMeter({ label, used, max }) {
  const isUnlimited = max <= 0
  const pct = isUnlimited ? 100 : Math.min((used / max) * 100, 100)
  const color = isUnlimited ? "bg-brand-500" : pct > 85 ? "bg-trading-red" : pct > 60 ? "bg-trading-yellow" : "bg-trading-green"
  return (
    <div>
      <div className="flex justify-between text-xs text-gray-400 mb-1">
        <span>{label}</span>
        <span className="font-medium text-white">{used}{isUnlimited ? " / ∞" : ` / ${max}`}</span>
      </div>
      <div className="h-2 bg-surface-200 rounded-full overflow-hidden">
        {isUnlimited
          ? <div className="h-full w-full bg-gradient-to-r from-brand-500/40 via-brand-500 to-brand-500/40 animate-pulse rounded-full"/>
          : <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }}/>
        }
      </div>
    </div>
  )
}

function PlanCard({ plan, current, onUpgrade, loading }) {
  const Icon = plan.icon
  const isCurrent = current?.plan_id === plan.id
  return (
    <div className={`card relative border ${plan.color} ${isCurrent ? "bg-brand-500/5" : ""} ${plan.popular ? "ring-1 ring-brand-500/30" : ""}`}>
      {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-500 text-white text-[10px] font-bold px-3 py-0.5 rounded-full">MOST POPULAR</div>}
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2.5 rounded-xl ${plan.id === "elite" ? "bg-trading-yellow/10" : plan.id === "pro" ? "bg-brand-500/10" : "bg-surface-100"}`}>
          <Icon size={18} className={plan.id === "elite" ? "text-trading-yellow" : plan.id === "pro" ? "text-brand-400" : "text-gray-400"}/>
        </div>
        <div>
          <h3 className="font-bold text-white">{plan.name}</h3>
          <Badge variant={plan.badge}>{plan.name}</Badge>
        </div>
        <div className="ml-auto text-right">
          {plan.price === 0 ? <p className="text-2xl font-bold text-white">Free</p> : <p className="text-2xl font-bold text-white">${plan.price}<span className="text-sm text-gray-400 font-normal">/mo</span></p>}
        </div>
      </div>
      <ul className="space-y-2 mb-5">
        {plan.features.map(f => (
          <li key={f} className="flex items-center gap-2 text-xs text-gray-300">
            <Check size={12} className="text-trading-green shrink-0"/>{f}
          </li>
        ))}
      </ul>
      {isCurrent ? (
        <button className="w-full btn-secondary text-sm opacity-60 cursor-default">Current Plan</button>
      ) : (
        <button onClick={() => onUpgrade(plan.id)} disabled={loading} className={`w-full text-sm ${plan.id === "free" ? "btn-secondary" : "btn-primary"}`}>
          {plan.price === 0 ? "Downgrade" : `Upgrade to ${plan.name}`}
        </button>
      )}
    </div>
  )
}

export default function Subscription() {
  const [sub, setSub] = useState(null)
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [upgrading, setUpgrading] = useState(false)
  const [showCancel, setShowCancel] = useState(false)

  useEffect(() => {
    Promise.all([
      api.get("/subscriptions/my").catch(() => ({ data: { plan_id: "free", status: "active", bots_used: 0, backtests_used: 3, exchanges_connected: 1 } })),
      api.get("/subscriptions/invoices").catch(() => ({ data: MOCK_INVOICES })),
    ]).then(([subRes, invRes]) => {
      setSub(subRes.data)
      setInvoices(invRes.data?.length ? invRes.data : MOCK_INVOICES)
    }).finally(() => setLoading(false))
  }, [])

  async function upgrade(planId) {
    if (planId === "free") { setShowCancel(true); return }
    setUpgrading(true)
    try {
      const { data } = await api.post("/subscriptions/checkout", { plan_id: planId })
      if (data.checkout_url) window.open(data.checkout_url, "_blank")
      else toast.success("Upgrade initiated!")
    } catch { toast.error("Checkout unavailable. Contact support.") }
    finally { setUpgrading(false) }
  }

  async function cancel() {
    try {
      await api.post("/subscriptions/cancel")
      toast.success("Subscription cancelled")
      setSub(s => ({...s, status: "cancelled", cancel_at: new Date(Date.now() + 30*86400000).toISOString() }))
    } catch { toast.error("Failed to cancel subscription") }
    setShowCancel(false)
  }

  const currentPlan = PLANS.find(p => p.id === (sub?.plan_id || "free")) || PLANS[0]

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white">Subscription</h2>
        <p className="text-sm text-gray-400 mt-1">Manage your plan and billing</p>
      </div>

      {/* Current plan summary */}
      <div className="card">
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-1">Current Plan</p>
            <div className="flex items-center gap-3">
              <h3 className="text-2xl font-bold text-white">{currentPlan.name}</h3>
              <Badge variant={sub?.status === "active" ? "green" : "red"}>{sub?.status || "active"}</Badge>
            </div>
            {sub?.current_period_end && <p className="text-xs text-gray-400 mt-1">Renews {formatDate(sub.current_period_end)}</p>}
            {sub?.cancel_at && <p className="text-xs text-trading-yellow mt-1">Cancels {formatDate(sub.cancel_at)}</p>}
          </div>
          {currentPlan.id !== "free" && (
            <button onClick={() => setShowCancel(true)} className="btn-secondary text-xs flex items-center gap-1.5 text-trading-red border-trading-red/20 hover:bg-trading-red/10"><X size={12}/>Cancel</button>
          )}
        </div>
        <div className="space-y-3">
          <UsageMeter label="Bots" used={sub?.bots_used ?? 0} max={currentPlan.limits.bots}/>
          <UsageMeter label="Backtests this month" used={sub?.backtests_used ?? 0} max={currentPlan.limits.backtests}/>
          <UsageMeter label="Exchange connections" used={sub?.exchanges_connected ?? 0} max={currentPlan.limits.exchanges}/>
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
        {PLANS.map(plan => <PlanCard key={plan.id} plan={plan} current={sub} onUpgrade={upgrade} loading={upgrading}/>)}
      </div>

      {/* Invoice history */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <Receipt size={16} className="text-gray-400"/>
          <h3 className="font-semibold text-white">Invoice History</h3>
        </div>
        <div className="space-y-2">
          {invoices.map(inv => (
            <div key={inv.id} className="flex items-center justify-between p-3 bg-surface-100 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-500/10 rounded-lg"><CreditCard size={13} className="text-brand-400"/></div>
                <div>
                  <p className="text-sm font-medium text-white">{inv.plan} Plan</p>
                  <p className="text-xs text-gray-400">{inv.date}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="green">{inv.status}</Badge>
                <span className="font-mono font-semibold text-white">${inv.amount}</span>
              </div>
            </div>
          ))}
          {invoices.length === 0 && <p className="text-gray-500 text-sm text-center py-4">No invoices yet</p>}
        </div>
      </div>

      {showCancel && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 w-full max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-trading-red/10 rounded-xl"><AlertCircle size={20} className="text-trading-red"/></div>
              <h3 className="font-bold text-white">Cancel Subscription?</h3>
            </div>
            <p className="text-sm text-gray-400 mb-6">Your plan will remain active until the end of the billing period. After that, you'll be downgraded to the Free plan.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowCancel(false)} className="btn-secondary flex-1">Keep Plan</button>
              <button onClick={cancel} className="flex-1 px-4 py-2.5 rounded-xl bg-trading-red/10 text-trading-red border border-trading-red/20 hover:bg-trading-red/20 text-sm font-medium">Cancel Plan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
